import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ErrorText } from '@/components/ui';
import { authClient, authErrorMessage } from '@/lib/auth-client';
import { useClearUrlSecret } from '@/lib/url-secrets';
import { AuthLayout } from './AuthLayout';

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const invalid = params.get('error');
  useClearUrlSecret('/reset-password');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return setError('This reset link is missing its token. Request a new one.');
    if (password !== confirm) return setError('Passwords do not match');
    setBusy(true);
    setError(null);
    const offline: { error: { status: number; code?: string; message?: string } } = {
      error: { status: 0 },
    };
    const res = await authClient
      .resetPassword({ newPassword: password, token })
      .catch(() => offline);
    setBusy(false);
    if (res.error) {
      if (res.error.code === 'INVALID_TOKEN')
        return setError('This link has already been used or has expired. Request a new one.');
      // No answer, or the server broke: the link may still be good, so say try again.
      if (!res.error.status || res.error.status >= 500)
        return setError("We couldn't save your new password. Check your connection and try again.");
      return setError(authErrorMessage(res.error));
    }
    void navigate('/login', {
      replace: true,
      state: { toast: 'Password updated · log in with your new password' },
    });
  };

  return (
    <AuthLayout
      title="Choose a new password."
      sub="At least 8 characters. Once it's saved, every device is signed out and you log in again with the new password."
    >
      {invalid || !token ? (
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>
          This reset link is invalid or has expired.{' '}
          <Link to="/forgot-password" style={{ fontWeight: 600 }}>
            Request a new one
          </Link>
          .
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'contents' }}>
          <input
            className="input"
            type="password"
            placeholder="New password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-label="New password"
            minLength={8}
            required
          />
          <input
            className="input"
            type="password"
            placeholder="Confirm password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            aria-label="Confirm password"
            minLength={8}
            required
          />
          <ErrorText>{error}</ErrorText>
          <button type="submit" className="btn btn-primary btn-lg hv" disabled={busy}>
            {busy ? 'Saving…' : 'Update password'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
